"use client";

import React, { useCallback } from "react";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import OptimizedImage from "../common/OptimizedImage";
import ContentTag from "../../utils/tag";
import { useInputMode } from "../../utils/hooks/useInputMode";
import LiveStartCountdown from "../common/LiveStartCountdown";
import {
  getDisplayYear,
  getFutureLiveStartTimestamp,
  isLiveContent,
} from "../../utils/liveStartCountdown";
import { focusHomeMenu } from "./focusHelpers";

const MovieItem = ({ item = {}, onFocus, isFirst, focusKey }) => {
  const navigate = useNavigate();
  const { contentPhotos, title, year, slug, tags, kind, contentType } = item || {};
  const liveStartTimestamp = isLiveContent(item) ? getFutureLiveStartTimestamp(item) : null;
  const displayYear = getDisplayYear(year);
  const navigationUrl = `/${contentType?.kind || kind || "movie"}/${slug}`;
  const { focused, ref, focusSelf } = useFocusable({
    focusKey,
    onFocus,
    onEnterPress: () => {
      navigate(navigationUrl);
    },
    onArrowPress: (direction) => {
      if (direction === "right" && isFirst) {
        return focusHomeMenu();
      }
      return true;
    },
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Get optimized image with LQIP and WebP support
  const imageData = getOptimizedImageForType("verticalCover", contentPhotos, "TV", {
    width: 200,
    quality: 90,
    format: 'jpeg'
  });

  return (
    <div
      ref={ref}
      onClick={() => navigate(navigationUrl)}
      onMouseEnter={handleMouseEnter}
      className={"legacy-penta-card" + (focused ? " is-focused" : "")}
    >
      <OptimizedImage
        src={imageData.src}
        className="legacy-penta-card-image"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "15px",
        }}
      />
      {tags?.[0] && <ContentTag label={tags?.[0]} />}
      <div className="legacy-penta-focus-border" />
      <div className="legacy-penta-overlay">
        <span className="legacy-penta-title">{title}</span>
        {displayYear && <span className="legacy-penta-year">{displayYear}</span>}
      </div>
      {liveStartTimestamp && (
        <LiveStartCountdown
          startTimestamp={liveStartTimestamp}
          style={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 15,
            justifyContent: "center",
          }}
        />
      )}
    </div>
  );
};

const PentaBanners = ({
  bannerTitle,
  bannerDescription,
  bannerBackground,
  bannerItems,
  onFocus,
  rowTitle,
  id,
  index,
}) => {
  const itemFocusKeyPrefix = "penta_banner_item_" + id + "_";
  const { ref, focusKey } = useFocusable({
    focusKey: "penta-banner" + id,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
    focusable: false,
    trackChildren: true,
    autoRestoreFocus: true,
    preferredChildFocusKey: itemFocusKeyPrefix + "0",
  });

  const scrollingRef = useRef(null);
  const innerRef = useRef(null);

  if (bannerItems.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className="legacy-penta-section"
        ref={ref}
        tabIndex={index}
      >
        <div className="legacy-penta-banner-container">
          <div className="legacy-penta-banner-bg" style={{ backgroundImage: "url(" + bannerBackground + ")" }}>
            <div className="legacy-penta-banner-copy">
              <h3 className="legacy-penta-banner-title">{bannerTitle}</h3>
              <h5 className="legacy-penta-banner-description">{bannerDescription}</h5>
            </div>
          </div>
        </div>
        <div className="legacy-penta-items-wrap">
          <div className="legacy-penta-row">
            <h2 className="legacy-penta-row-title">{rowTitle}</h2>
            <div className="legacy-penta-scroller" ref={scrollingRef}>
              <div className="legacy-penta-content" ref={innerRef}>
                {bannerItems?.map((item, idx) => {
                  return (
                    <MovieItem
                      item={item.content}
                      key={"penta_content_" + idx + id}
                      isFirst={idx === 0}
                      focusKey={itemFocusKeyPrefix + idx}
                      onFocus={onFocus}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};

export default PentaBanners;
